/**
 * Planning (scherm 05) als agenda, naar het voorbeeld van de agenda in Outlook.
 *
 * Bovenin de bouwnummerfilter: alle bouwnummers in één agenda (de machines werken voor
 * alle boten tegelijk), of één. Links het navigatiedeelvenster: een minikalender en wat
 * nog in te plannen is, per bouwnummer en sectie gegroepeerd. Rechts de agenda met de
 * weergaven Dag, Drie dagen, Werkweek, Week, Maand en Lijst, de knop Vandaag, vorige en
 * volgende, en de weergave-instellingen: tijdschaal, alleen werkuren en bouwnummers
 * naast elkaar.
 *
 * Elke kar is een afspraak. Slepen uit de lijst zet hem in de agenda met de lengte
 * van zijn werk (de houtstaat-uren, of anders de begrote uren van de machinale),
 * gerekend in werkuren: 20 uur vanaf maandag 07:00 loopt tot woensdag 09:00. Een
 * afspraak verschuift met slepen of met de pijltjes, de onderrand rekt hem op, en
 * terugslepen naar de lijst (of Delete) haalt hem eruit. Een klik toont de details.
 * Een kar die nog niet is vrijgegeven staat er gearceerd in: voorlopig.
 *
 * Elke handeling wordt meteen bewaard; een kar die on-hold staat weigert de API.
 */
import {
  STANDAARD_WERKUREN,
  leesTijdstip,
  plusWerkminuten,
  schrijfTijdstip,
  werkminutenTussen,
} from '@houtstaat/engine';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';

import { api, type AgendaKar, type Bouwnummer } from '../api';
import { bouwnummerKort, useGegevens } from '../gegevens';
import { Bouwnummerfilter } from '../onderdelen/Bouwnummerfilter';
import { Schakelaar } from '../onderdelen/Dialoog';
import {
  IconChevronLinks,
  IconChevronOnder,
  IconChevronRechts,
  IconGreep,
  IconKruis,
  IconZoek,
} from '../onderdelen/Iconen';
import {
  WEERGAVEN,
  dagBegin,
  dagenVan,
  isTijdraster,
  klok,
  klokVan,
  legIndeling,
  maandraster,
  minuutVanDag,
  opMinuut,
  periodetitel,
  plusDagen,
  segmenten,
  tijdvak,
  urenbereik,
  verschuif,
  weekdagKort,
  weekdagLang,
  weeknummer,
  zelfdeDag,
  type Segment,
  type Weergave,
} from '../onderdelen/kalender';
import { Maatlijn } from '../onderdelen/Maatlijn';

const WERKUREN = STANDAARD_WERKUREN;
const SLOT = 24; // px per tijdschaalvak
const BEWAARD = 'houtstaat.agenda';
/** Zoveel karren toont een open groep; de rest vind je met zoeken. */
const MAX_PER_GROEP = 80;

type Tijdschaal = 15 | 30 | 60;

interface Voorkeur {
  weergave: Weergave;
  tijdschaal: Tijdschaal;
  alleenWerkuren: boolean;
  naastElkaar: boolean;
  /** Eén bouwnummer, of `null` voor alle bouwnummers in één agenda. */
  bouwnummer: string | null;
}

const STANDAARD: Voorkeur = {
  weergave: 'werkweek',
  tijdschaal: 30,
  alleenWerkuren: true,
  naastElkaar: false,
  bouwnummer: null,
};

function leesVoorkeur(): Voorkeur {
  try {
    return { ...STANDAARD, ...(JSON.parse(localStorage.getItem(BEWAARD) ?? '{}') as Partial<Voorkeur>) };
  } catch {
    return STANDAARD;
  }
}

/** Een afspraak zoals hij getekend wordt: met een vlag als hij nu wordt gesleept. */
type Item = AgendaKar & { sleept?: boolean };

interface Sleep {
  soort: 'nieuw' | 'verplaats' | 'rek';
  kar: AgendaKar;
  /** Lengte van het werk in werkminuten; blijft gelijk bij verplaatsen. */
  werkduur: number;
  /** Bij verplaatsen: de minuten tussen het begin van de afspraak en waar hij is vastgepakt. */
  greep: number;
  voorbeeld: { start: string; eind: string } | null;
  x: number;
  y: number;
  vanX: number;
  vanY: number;
  bewogen: boolean;
  boventLijst: boolean;
}

const uren = (n: number | null | undefined): string =>
  `${(Math.round((n ?? 0) * 10) / 10).toLocaleString('nl-NL')} u`;

const isVoorlopig = (k: AgendaKar): boolean => k.status === 'concept';

type Soort = 'alle' | 'vrijgegeven' | 'voorlopig';
const karTitel = (k: AgendaKar): string => [k.karNaam, k.ruimte].filter(Boolean).join(' · ');

/** De eerste werkminuut op of na `d`. */
const naarWerktijd = (d: Date): Date => new Date(plusWerkminuten(d, 1, WERKUREN).getTime() - 60_000);

const werkduurVan = (k: AgendaKar): number =>
  k.start && k.eind
    ? werkminutenTussen(leesTijdstip(k.start), leesTijdstip(k.eind), WERKUREN) || 60
    : Math.max(60, Math.round(k.urenAfspraak * 60));

export function Planning() {
  const { mag, meldFout, wijzigingsteller, kiesBoot } = useGegevens();
  const navigeer = useNavigate();
  const magWijzigen = mag('planningWijzigen');

  const [data, setData] = useState<{ bouwnummers: Bouwnummer[]; karren: AgendaKar[] } | null>(null);
  const [voorkeur, setVoorkeur] = useState(leesVoorkeur);
  const [datum, setDatum] = useState(() => dagBegin(new Date()));
  const [nu, setNu] = useState(() => new Date());
  const [zoek, setZoek] = useState('');
  const [soort, setSoort] = useState<Soort>('alle');
  const [gekozen, setGekozen] = useState<{ id: string; rect: DOMRect } | null>(null);
  const [sleep, setSleep] = useState<Sleep | null>(null);
  const sleepRef = useRef<Sleep | null>(null);
  sleepRef.current = sleep;

  const { weergave, tijdschaal, alleenWerkuren, naastElkaar } = voorkeur;
  const zetVoorkeur = (wijziging: Partial<Voorkeur>) => {
    setVoorkeur((v) => {
      const nieuw = { ...v, ...wijziging };
      try {
        localStorage.setItem(BEWAARD, JSON.stringify(nieuw));
      } catch {
        // Zonder opslag geldt de keuze tot de pagina herlaadt.
      }
      return nieuw;
    });
  };

  const haal = useCallback(async () => {
    try {
      const antwoord = await api.planning('alles');
      setData({ bouwnummers: antwoord.bouwnummers, karren: antwoord.karren });
    } catch (e) {
      meldFout(e instanceof Error ? e.message : 'De planning kon niet geladen worden.');
    }
  }, [meldFout]);

  // Wijzigingen van anderen komen binnen, behalve midden in het slepen.
  useEffect(() => {
    if (!sleepRef.current) void haal();
  }, [haal, wijzigingsteller]);

  // De nu-lijn schuift elke minuut mee.
  useEffect(() => {
    const tikker = window.setInterval(() => setNu(new Date()), 60_000);
    return () => window.clearInterval(tikker);
  }, []);

  const boten = useMemo(() => data?.bouwnummers ?? [], [data]);
  const kleurVan = useMemo(() => new Map(boten.map((b) => [b.bouwnummer, b.kleur])), [boten]);
  // Een bewaard bouwnummer dat er niet meer is, telt als "alle".
  const filter = boten.some((b) => b.bouwnummer === voorkeur.bouwnummer) ? voorkeur.bouwnummer : null;
  const zichtbareBoten = filter ? boten.filter((b) => b.bouwnummer === filter) : boten;

  const alleKarren = useMemo(() => data?.karren ?? [], [data]);
  const karren = useMemo(
    () => (filter ? alleKarren.filter((k) => k.bouwnummer === filter) : alleKarren),
    [alleKarren, filter],
  );
  const gepland = useMemo(() => karren.filter((k) => k.start && k.eind), [karren]);

  /** Wat er getekend wordt: de geplande karren, met de gesleepte kar op zijn nieuwe plek. */
  const items: Item[] = useMemo(() => {
    if (!sleep) return gepland;
    const rest = gepland.filter((k) => k.id !== sleep.kar.id);
    if (sleep.voorbeeld) return [...rest, { ...sleep.kar, ...sleep.voorbeeld, sleept: true }];
    return sleep.soort === 'nieuw' ? rest : [...rest, { ...sleep.kar, sleept: true }];
  }, [gepland, sleep]);

  const dagen = useMemo(() => dagenVan(weergave, datum), [weergave, datum]);
  const bereik = urenbereik(alleenWerkuren, WERKUREN);

  // ── Bewaren ────────────────────────────────────────────────────────────────

  const bewaar = useCallback(
    async (kar: AgendaKar, start: string | null, eind: string | null) => {
      setData((d) =>
        d
          ? {
              ...d,
              karren: d.karren.map((k) =>
                k.id === kar.id ? { ...k, start: start ?? undefined, eind: eind ?? undefined } : k,
              ),
            }
          : d,
      );
      try {
        await api.planningKar(kar.bouwnummer, kar.id, start, eind);
      } catch (e) {
        meldFout(e instanceof Error ? e.message : 'De planning kon niet bewaard worden.');
        await haal();
      }
    },
    [haal, meldFout],
  );

  const verschuifMet = (kar: AgendaKar, minuten: number) => {
    if (!kar.start) return;
    let start = new Date(leesTijdstip(kar.start).getTime() + minuten * 60_000);
    if (alleenWerkuren) start = naarWerktijd(start);
    void bewaar(kar, schrijfTijdstip(start), schrijfTijdstip(plusWerkminuten(start, werkduurVan(kar), WERKUREN)));
  };

  // ── Slepen ─────────────────────────────────────────────────────────────────

  /** Waar de muis nu op het raster wijst: een dag, en in het tijdraster ook een minuut. */
  const doelOnder = (x: number, y: number): { dag: Date; minuut: number | null } | null => {
    const el = document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-dag]');
    if (!el?.dataset.dag) return null;
    const dag = leesTijdstip(`${el.dataset.dag}T00:00`);
    if (el.dataset.tijd == null) return { dag, minuut: null };
    const rect = el.getBoundingClientRect();
    const uurHoogte = SLOT * (60 / tijdschaal);
    return { dag, minuut: bereik[0] + ((y - rect.top) / uurHoogte) * 60 };
  };

  const voorbeeldVoor = (s: Sleep, x: number, y: number): Sleep['voorbeeld'] => {
    const doel = doelOnder(x, y);
    if (!doel) return null;
    const rond = (m: number) => Math.round(m / tijdschaal) * tijdschaal;

    if (s.soort === 'rek') {
      if (!s.kar.start || doel.minuut == null) return s.voorbeeld;
      const start = leesTijdstip(s.kar.start);
      const eind = opMinuut(doel.dag, rond(doel.minuut));
      if (eind.getTime() - start.getTime() < tijdschaal * 60_000) return s.voorbeeld;
      return { start: s.kar.start, eind: schrijfTijdstip(eind) };
    }

    let start: Date;
    if (doel.minuut == null) {
      // Maand: de dag verandert, het tijdstip blijft (of het begin van de werkdag).
      const oud = s.kar.start ? leesTijdstip(s.kar.start) : null;
      const minuut = oud ? minuutVanDag(oud) : WERKUREN.begin;
      start = plusDagen(opMinuut(doel.dag, minuut), -Math.round(s.greep / (24 * 60)));
    } else {
      start = opMinuut(doel.dag, rond(doel.minuut - s.greep));
    }
    if (alleenWerkuren) start = naarWerktijd(start);
    return { start: schrijfTijdstip(start), eind: schrijfTijdstip(plusWerkminuten(start, s.werkduur, WERKUREN)) };
  };

  const begin = (e: React.PointerEvent, soort: Sleep['soort'], kar: AgendaKar, greep = 0) => {
    if (e.button !== 0) return;
    e.preventDefault();
    setGekozen(null);
    setSleep({
      soort,
      kar,
      werkduur: werkduurVan(kar),
      greep,
      voorbeeld: null,
      x: e.clientX,
      y: e.clientY,
      vanX: e.clientX,
      vanY: e.clientY,
      bewogen: false,
      boventLijst: false,
    });
  };

  useEffect(() => {
    if (!sleep) return;
    const beweeg = (e: PointerEvent) => {
      const s = sleepRef.current;
      if (!s) return;
      const bewogen = s.bewogen || Math.hypot(e.clientX - s.vanX, e.clientY - s.vanY) > 4;
      if (!bewogen) return;
      const boventLijst = Boolean(
        document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-wachtrij]'),
      );
      setSleep({
        ...s,
        bewogen,
        x: e.clientX,
        y: e.clientY,
        boventLijst,
        voorbeeld: boventLijst ? null : voorbeeldVoor(s, e.clientX, e.clientY),
      });
    };
    const los = (e: PointerEvent) => {
      const s = sleepRef.current;
      setSleep(null);
      if (!s) return;
      if (!s.bewogen) {
        // Een klik zonder te slepen: de details tonen.
        if (s.soort !== 'nieuw') {
          const el = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-afspraak]');
          if (el) setGekozen({ id: s.kar.id, rect: el.getBoundingClientRect() });
        }
        return;
      }
      if (s.boventLijst && s.soort !== 'nieuw') void bewaar(s.kar, null, null);
      else if (s.voorbeeld) void bewaar(s.kar, s.voorbeeld.start, s.voorbeeld.eind);
    };
    const stop = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSleep(null);
    };
    window.addEventListener('pointermove', beweeg);
    window.addEventListener('pointerup', los);
    window.addEventListener('keydown', stop);
    return () => {
      window.removeEventListener('pointermove', beweeg);
      window.removeEventListener('pointerup', los);
      window.removeEventListener('keydown', stop);
    };
    // Alleen opnieuw koppelen als er een sleep begint of eindigt; de rest leest de ref.
  }, [Boolean(sleep)]);

  // ── Afspraak met het toetsenbord ──────────────────────────────────────────

  const opToets = (e: React.KeyboardEvent, kar: AgendaKar) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setGekozen({ id: kar.id, rect: (e.currentTarget as HTMLElement).getBoundingClientRect() });
      return;
    }
    if (!magWijzigen) return;
    const stap: Record<string, number> = {
      ArrowUp: -tijdschaal,
      ArrowDown: tijdschaal,
      ArrowLeft: -24 * 60,
      ArrowRight: 24 * 60,
    };
    if (e.key in stap) {
      e.preventDefault();
      verschuifMet(kar, stap[e.key]!);
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      void bewaar(kar, null, null);
    }
  };

  const afspraakProps = (kar: Item, greep: (e: React.PointerEvent) => number) => ({
    'data-afspraak': kar.id,
    tabIndex: 0,
    role: 'button',
    'aria-label': `${kar.id} ${karTitel(kar)}${kar.start && kar.eind ? `, ${tijdvak(leesTijdstip(kar.start), leesTijdstip(kar.eind))}` : ''}${isVoorlopig(kar) ? ', voorlopig' : ''}`,
    onKeyDown: (e: React.KeyboardEvent) => opToets(e, kar),
    onPointerDown: (e: React.PointerEvent) => {
      if (magWijzigen) begin(e, 'verplaats', kar, greep(e));
      else if (e.button === 0) setGekozen({ id: kar.id, rect: e.currentTarget.getBoundingClientRect() });
    },
    style: { '--boot': kleurVan.get(kar.bouwnummer) ?? '#808080' } as CSSProperties,
  });

  // ── Weergave ──────────────────────────────────────────────────────────────

  const vandaag = dagBegin(nu);
  const ingepland = karren.filter((k) => k.start).length;
  const gekozenKar = gekozen ? karren.find((k) => k.id === gekozen.id) ?? null : null;

  if (!data) return <div className="hs-leeg">De planning wordt geladen…</div>;

  return (
    <>
      <header className="hs-kop">
        <div className="hs-koprij">
          <div>
            <p className="hs-annotatie hs-kop__gebied">Werkvoorbereiding</p>
            <h1 className="hs-h1">Planning</h1>
            <Maatlijn />
            <p className="hs-subregel">
              {filter ? `Bouwnummer ${bouwnummerKort(filter)}` : 'Alle bouwnummers in één agenda van de werkplaats'} ·{' '}
              {ingepland} karren ingepland · {karren.length - ingepland} nog in te plannen
              {magWijzigen ? '' : ' · je hebt alleen leesrechten'}
            </p>
            <Bouwnummerfilter
              metAlle
              waarde={filter}
              onKies={(nr) => {
                zetVoorkeur({ bouwnummer: nr });
                // Eén bouwnummer kiezen geldt ook voor de andere schermen.
                if (nr) kiesBoot(nr);
              }}
              teller={(nr) => alleKarren.filter((k) => !k.start && (!nr || k.bouwnummer === nr)).length}
            />
          </div>
        </div>
      </header>

      <div className={`hs-agenda${sleep?.bewogen ? ' hs-agenda--sleept' : ''}`}>
        <aside className="hs-agenda__paneel">
          <Minikalender
            datum={datum}
            vandaag={vandaag}
            zichtbaar={dagen}
            weergave={weergave}
            gepland={gepland}
            onKies={setDatum}
          />

          <Wachtrij
            karren={karren.filter((k) => !k.start)}
            eenBoot={Boolean(filter)}
            zoek={zoek}
            onZoek={setZoek}
            soort={soort}
            onSoort={setSoort}
            kleurVan={kleurVan}
            magWijzigen={magWijzigen}
            actief={Boolean(sleep?.bewogen && sleep.boventLijst && sleep.soort !== 'nieuw')}
            onSleep={(e, kar) => begin(e, 'nieuw', kar)}
          />
        </aside>

        <section className="hs-agenda__hoofd" aria-label="Agenda">
          <div className="hs-agenda__werkbalk">
            <div className="hs-agenda__navigatie">
              <button type="button" className="hs-knop" onClick={() => setDatum(vandaag)}>
                Vandaag
              </button>
              <button
                type="button"
                className="hs-icoonknop"
                onClick={() => setDatum(verschuif(weergave, datum, -1))}
                aria-label="Vorige"
                title="Vorige"
              >
                <IconChevronLinks maat={20} />
              </button>
              <button
                type="button"
                className="hs-icoonknop"
                onClick={() => setDatum(verschuif(weergave, datum, 1))}
                aria-label="Volgende"
                title="Volgende"
              >
                <IconChevronRechts maat={20} />
              </button>
              <h2 className="hs-agenda__titel">{periodetitel(weergave, dagen, datum)}</h2>
              {isTijdraster(weergave) ? (
                <span className="hs-annotatie hs-muted">week {weeknummer(dagen[0]!)}</span>
              ) : null}
            </div>

            <div className="hs-agenda__weergaven">
              <div className="hs-segment" role="radiogroup" aria-label="Weergave">
                {WEERGAVEN.map((w) => (
                  <button
                    key={w.waarde}
                    type="button"
                    role="radio"
                    aria-checked={weergave === w.waarde}
                    className={`hs-segment__optie${weergave === w.waarde ? ' actief' : ''}`}
                    onClick={() => zetVoorkeur({ weergave: w.waarde })}
                  >
                    {w.label}
                  </button>
                ))}
              </div>
              <Weergaveopties voorkeur={voorkeur} onWijzig={zetVoorkeur} />
            </div>
          </div>

          {isTijdraster(weergave) ? (
            <Tijdraster
              dagen={dagen}
              items={items}
              bereik={bereik}
              tijdschaal={tijdschaal}
              boten={naastElkaar ? zichtbareBoten : null}
              alleenWerkdagen={alleenWerkuren}
              nu={nu}
              magWijzigen={magWijzigen}
              gekozen={gekozen?.id ?? null}
              afspraakProps={afspraakProps}
              onRek={(e, kar) => begin(e, 'rek', kar)}
              onKiesDag={(dag) => {
                setDatum(dag);
                zetVoorkeur({ weergave: 'dag' });
              }}
            />
          ) : weergave === 'maand' ? (
            <Maandraster
              datum={datum}
              vandaag={vandaag}
              items={items}
              gekozen={gekozen?.id ?? null}
              afspraakProps={afspraakProps}
              onKiesDag={(dag) => {
                setDatum(dag);
                zetVoorkeur({ weergave: 'dag' });
              }}
            />
          ) : (
            <Lijstweergave
              dagen={dagen}
              items={items}
              kleurVan={kleurVan}
              onKies={(kar, rect) => setGekozen({ id: kar.id, rect })}
            />
          )}
        </section>
      </div>

      {sleep?.bewogen && !sleep.voorbeeld ? (
        <div
          className={`hs-agenda__spook${sleep.boventLijst ? ' terug' : ''}`}
          style={{ left: sleep.x + 14, top: sleep.y + 14, '--boot': kleurVan.get(sleep.kar.bouwnummer) } as CSSProperties}
          aria-hidden
        >
          {sleep.boventLijst ? 'Uit de planning halen: ' : ''}
          <span className="hs-mono">{sleep.kar.id}</span>
        </div>
      ) : null}

      {gekozen && gekozenKar ? (
        <Details
          kar={gekozenKar}
          rect={gekozen.rect}
          kleur={kleurVan.get(gekozenKar.bouwnummer) ?? '#808080'}
          magWijzigen={magWijzigen}
          onSluit={() => setGekozen(null)}
          onUitPlanning={() => {
            setGekozen(null);
            void bewaar(gekozenKar, null, null);
          }}
          onHoutstaat={() => {
            kiesBoot(gekozenKar.bouwnummer);
            navigeer(`/houtstaat/${gekozenKar.id}`);
          }}
        />
      ) : null}
    </>
  );
}

type AfspraakProps = (kar: Item, greep: (e: React.PointerEvent) => number) => Record<string, unknown>;

// ── Tijdraster: Dag, Drie dagen, Werkweek, Week ──────────────────────────────

function Tijdraster({
  dagen,
  items,
  bereik,
  tijdschaal,
  boten,
  alleenWerkdagen,
  nu,
  magWijzigen,
  gekozen,
  afspraakProps,
  onRek,
  onKiesDag,
}: {
  dagen: Date[];
  items: Item[];
  bereik: [number, number];
  tijdschaal: Tijdschaal;
  /** Bij "naast elkaar": de bouwnummers als eigen kolom binnen elke dag. */
  boten: Bouwnummer[] | null;
  alleenWerkdagen: boolean;
  nu: Date;
  magWijzigen: boolean;
  gekozen: string | null;
  afspraakProps: AfspraakProps;
  onRek: (e: React.PointerEvent, kar: AgendaKar) => void;
  onKiesDag: (dag: Date) => void;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const uurHoogte = SLOT * (60 / tijdschaal);
  const hoogte = ((bereik[1] - bereik[0]) / 60) * uurHoogte;
  const yVan = (minuut: number) => ((minuut - bereik[0]) / 60) * uurHoogte;

  // De hele dag in beeld: open op het begin van de werkdag, zoals Outlook.
  useLayoutEffect(() => {
    if (scroller.current) scroller.current.scrollTop = Math.max(0, yVan(WERKUREN.begin) - 8);
  }, [bereik[0], tijdschaal]);

  const kolommen = boten && boten.length > 0 ? boten : null;
  const telt = (dag: Date) => !alleenWerkdagen || WERKUREN.dagen.includes(dag.getDay());
  const segs = legIndeling(segmenten(items, dagen, bereik, telt), (s) =>
    kolommen ? `${s.dag}|${s.item.bouwnummer}` : String(s.dag),
  );

  const uurLijnen: number[] = [];
  for (let m = bereik[0]; m < bereik[1]; m += 60) uurLijnen.push(m);
  const nuMinuut = minuutVanDag(nu);

  /** Werkuren per dag in de agenda, voor de kop: hoeveel er die dag ingepland staat. */
  const perDag = dagen.map((dag) =>
    items.reduce((n, k) => {
      if (!k.start || !k.eind) return n;
      const van = new Date(Math.max(leesTijdstip(k.start).getTime(), opMinuut(dag, 0).getTime()));
      const tot = new Date(Math.min(leesTijdstip(k.eind).getTime(), opMinuut(dag, 24 * 60).getTime()));
      return n + (tot > van ? werkminutenTussen(van, tot, WERKUREN) : 0);
    }, 0),
  );

  const datumSleutel = (dag: Date) => schrijfTijdstip(dag).slice(0, 10);

  const blok = (s: Segment<Item>) => {
    const kar = s.item;
    const top = yVan(s.van);
    const h = Math.max(SLOT - 4, yVan(s.tot) - top - 2);
    const start = kar.start ? leesTijdstip(kar.start) : null;
    const eind = kar.eind ? leesTijdstip(kar.eind) : null;
    return (
      <div
        key={`${kar.id}-${s.dag}`}
        className={[
          'hs-afspraak',
          isVoorlopig(kar) ? 'hs-afspraak--voorlopig' : '',
          kar.sleept ? 'hs-afspraak--sleept' : '',
          gekozen === kar.id ? 'hs-afspraak--gekozen' : '',
          s.begint ? '' : 'hs-afspraak--vervolg',
          s.eindigt ? '' : 'hs-afspraak--loopt-door',
          h < 40 ? 'hs-afspraak--smal' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        {...afspraakProps(kar, (e) => {
          // Waar in de afspraak is hij vastgepakt, in minuten vanaf het begin.
          if (!start) return 0;
          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
          const minuut = s.van + ((e.clientY - rect.top) / uurHoogte) * 60;
          return (opMinuut(dagen[s.dag]!, minuut).getTime() - start.getTime()) / 60_000;
        })}
        style={{
          ...(afspraakProps(kar, () => 0).style as CSSProperties),
          top,
          height: h,
          left: `calc(${(s.kolom / s.kolommen) * 100}% + 2px)`,
          width: `calc(${100 / s.kolommen}% - 4px)`,
        }}
      >
        <span className="hs-afspraak__titel">
          <span className="hs-mono">{kar.id}</span>
          {karTitel(kar) ? <span className="hs-afspraak__naam"> {karTitel(kar)}</span> : null}
        </span>
        {h >= 40 && start && eind ? (
          <span className="hs-afspraak__tijd">
            {s.begint ? klokVan(start) : '…'} – {s.eindigt ? klokVan(eind) : '…'}
            {isVoorlopig(kar) ? ' · voorlopig' : ''}
          </span>
        ) : null}
        {magWijzigen && s.eindigt && !kar.sleept ? (
          <span
            className="hs-afspraak__rek"
            aria-hidden
            onPointerDown={(e) => {
              e.stopPropagation();
              onRek(e, kar);
            }}
          />
        ) : null}
      </div>
    );
  };

  return (
    <div className="hs-tijdraster">
      <div
        className="hs-tijdraster__kop"
        style={{ gridTemplateColumns: `56px repeat(${dagen.length}, minmax(0, 1fr))` }}
      >
        <span />
        {dagen.map((dag, i) => {
          const isVandaag = zelfdeDag(dag, nu);
          return (
            <button
              key={i}
              type="button"
              className={`hs-tijdraster__dag${isVandaag ? ' vandaag' : ''}`}
              onClick={() => onKiesDag(dag)}
              title={weekdagLang.format(dag)}
            >
              <span className="hs-annotatie">{weekdagKort.format(dag).replace('.', '')}</span>
              <span className="hs-tijdraster__nummer">{dag.getDate()}</span>
              {perDag[i] ? <span className="hs-tijdraster__last">{uren(perDag[i]! / 60)}</span> : null}
              {kolommen ? (
                <span className="hs-tijdraster__boten">
                  {kolommen.map((b) => (
                    <span key={b.bouwnummer} className="hs-mono" style={{ borderColor: b.kleur }} title={b.bouwnummer}>
                      {/* In een brede weergave is er alleen plaats voor de kleur. */}
                      {dagen.length <= 3 ? b.bouwnummer : null}
                    </span>
                  ))}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="hs-tijdraster__scroll" ref={scroller}>
        <div
          className="hs-tijdraster__lichaam"
          style={{ gridTemplateColumns: `56px repeat(${dagen.length}, minmax(0, 1fr))`, height: hoogte }}
        >
          <div className="hs-tijdraster__uren">
            {uurLijnen.map((m) => (
              <span key={m} style={{ top: yVan(m) }}>
                {klok(m)}
              </span>
            ))}
          </div>

          {dagen.map((dag, i) => {
            const isVandaag = zelfdeDag(dag, nu);
            const werkdag = WERKUREN.dagen.includes(dag.getDay());
            const achtergrond = {
              // Vakken per tijdschaal, een sterkere lijn per uur, en buiten de werkuren grijs.
              '--uur': `${uurHoogte}px`,
              '--vak': `${SLOT}px`,
              '--werk-van': `${werkdag ? yVan(WERKUREN.begin) : 0}px`,
              '--werk-tot': `${werkdag ? yVan(WERKUREN.eind) : 0}px`,
            } as CSSProperties;
            const kolom = (bouwnummer: string | null) => (
              <div
                key={bouwnummer ?? 'dag'}
                className="hs-tijdraster__kolom"
                data-dag={datumSleutel(dag)}
                data-tijd=""
              >
                {segs
                  .filter((s) => s.dag === i && (bouwnummer == null || s.item.bouwnummer === bouwnummer))
                  .map(blok)}
              </div>
            );
            return (
              <div key={i} className={`hs-tijdraster__dagkolom${isVandaag ? ' vandaag' : ''}`} style={achtergrond}>
                {kolommen ? kolommen.map((b) => kolom(b.bouwnummer)) : kolom(null)}
                {isVandaag && nuMinuut >= bereik[0] && nuMinuut <= bereik[1] ? (
                  <span className="hs-tijdraster__nu" style={{ top: yVan(nuMinuut) }} aria-label={`Nu, ${klokVan(nu)}`} />
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Maand ───────────────────────────────────────────────────────────────────

function Maandraster({
  datum,
  vandaag,
  items,
  gekozen,
  afspraakProps,
  onKiesDag,
}: {
  datum: Date;
  vandaag: Date;
  items: Item[];
  gekozen: string | null;
  afspraakProps: AfspraakProps;
  onKiesDag: (dag: Date) => void;
}) {
  const dagen = maandraster(datum);
  const segs = segmenten(items, dagen, [0, 24 * 60]);
  const MAX = 3;
  return (
    <div className="hs-maandraster">
      {dagen.slice(0, 7).map((dag) => (
        <span key={dag.getDay()} className="hs-annotatie hs-maandraster__weekdag">
          {weekdagKort.format(dag).replace('.', '')}
        </span>
      ))}
      {dagen.map((dag, i) => {
        const hier = segs.filter((s) => s.dag === i).sort((a, b) => a.van - b.van);
        const buiten = dag.getMonth() !== datum.getMonth();
        return (
          <div
            key={i}
            className={`hs-maandraster__dag${buiten ? ' buiten' : ''}${zelfdeDag(dag, vandaag) ? ' vandaag' : ''}`}
            data-dag={schrijfTijdstip(dag).slice(0, 10)}
          >
            <button type="button" className="hs-maandraster__nummer" onClick={() => onKiesDag(dag)}>
              {dag.getDate() === 1 ? `${dag.getDate()} ${dag.toLocaleString('nl-NL', { month: 'short' })}` : dag.getDate()}
            </button>
            {hier.slice(0, MAX).map((s) => {
              const kar = s.item;
              const props = afspraakProps(kar, () => {
                // In de maand pak je een dag vast: het verschil met de begindag in minuten.
                const start = kar.start ? dagBegin(leesTijdstip(kar.start)) : dag;
                return Math.round((dag.getTime() - start.getTime()) / 60_000);
              });
              return (
                <div
                  key={kar.id}
                  className={`hs-maandchip${isVoorlopig(kar) ? ' hs-afspraak--voorlopig' : ''}${kar.sleept ? ' hs-afspraak--sleept' : ''}${gekozen === kar.id ? ' hs-afspraak--gekozen' : ''}`}
                  {...props}
                >
                  <span className="hs-maandchip__stip" />
                  {s.begint ? <span className="hs-maandchip__tijd">{klok(s.van)}</span> : null}
                  <span className="hs-mono">{kar.id}</span>
                </div>
              );
            })}
            {hier.length > MAX ? (
              <button type="button" className="hs-maandraster__meer" onClick={() => onKiesDag(dag)}>
                +{hier.length - MAX} meer
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// ── Lijst ───────────────────────────────────────────────────────────────────

function Lijstweergave({
  dagen,
  items,
  kleurVan,
  onKies,
}: {
  dagen: Date[];
  items: Item[];
  kleurVan: Map<string, string>;
  onKies: (kar: AgendaKar, rect: DOMRect) => void;
}) {
  // Elke afspraak één keer, op de dag waarop hij begint.
  const perDag = dagen
    .map((dag) => ({
      dag,
      karren: items
        .filter((k) => k.start && zelfdeDag(leesTijdstip(k.start), dag))
        .sort((a, b) => (a.start ?? '').localeCompare(b.start ?? '')),
    }))
    .filter((d) => d.karren.length > 0);

  if (perDag.length === 0) {
    return <div className="hs-leeg hs-agenda__leeg">In deze maand staat nog niets in de agenda.</div>;
  }

  return (
    <div className="hs-agendalijst">
      {perDag.map(({ dag, karren }) => (
        <section key={dag.toISOString()}>
          <h3 className="hs-agendalijst__dag">{weekdagLang.format(dag)}</h3>
          <ul>
            {karren.map((k) => (
              <li key={k.id}>
                <button
                  type="button"
                  className={`hs-agendalijst__rij${isVoorlopig(k) ? ' voorlopig' : ''}`}
                  style={{ '--boot': kleurVan.get(k.bouwnummer) } as CSSProperties}
                  onClick={(e) => onKies(k, e.currentTarget.getBoundingClientRect())}
                >
                  <span className="hs-agendalijst__tijd hs-mono">
                    {tijdvak(leesTijdstip(k.start!), leesTijdstip(k.eind!))}
                  </span>
                  <span className="hs-agendalijst__balk" />
                  <span>
                    <span className="hs-mono" style={{ fontWeight: 600 }}>
                      {k.id}
                    </span>{' '}
                    <span className="hs-muted">{karTitel(k)}</span>
                  </span>
                  <span className="hs-muted">
                    {uren(werkminutenTussen(leesTijdstip(k.start!), leesTijdstip(k.eind!), WERKUREN) / 60)} werk
                    {isVoorlopig(k) ? ' · voorlopig' : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

// ── Navigatiedeelvenster ────────────────────────────────────────────────────

function Minikalender({
  datum,
  vandaag,
  zichtbaar,
  weergave,
  gepland,
  onKies,
}: {
  datum: Date;
  vandaag: Date;
  zichtbaar: Date[];
  weergave: Weergave;
  gepland: AgendaKar[];
  onKies: (dag: Date) => void;
}) {
  const [maand, setMaand] = useState(() => new Date(datum.getFullYear(), datum.getMonth(), 1));
  useEffect(() => setMaand(new Date(datum.getFullYear(), datum.getMonth(), 1)), [datum]);

  const dagen = maandraster(maand);
  const metAfspraak = new Set<string>();
  for (const k of gepland) {
    if (!k.start || !k.eind) continue;
    for (let d = dagBegin(leesTijdstip(k.start)); d <= leesTijdstip(k.eind); d = plusDagen(d, 1)) {
      metAfspraak.add(d.toDateString());
      if (metAfspraak.size > 2000) break;
    }
  }
  const inBeeld = (dag: Date) =>
    weergave === 'maand'
      ? dag.getMonth() === datum.getMonth()
      : zichtbaar.some((z) => zelfdeDag(z, dag));

  return (
    <section className="hs-agenda__blok hs-minikalender">
      <div className="hs-minikalender__kop">
        <span className="hs-minikalender__maand">
          {maand.toLocaleString('nl-NL', { month: 'long', year: 'numeric' })}
        </span>
        <button
          type="button"
          className="hs-icoonknop"
          onClick={() => setMaand(new Date(maand.getFullYear(), maand.getMonth() - 1, 1))}
          aria-label="Vorige maand"
        >
          <IconChevronLinks />
        </button>
        <button
          type="button"
          className="hs-icoonknop"
          onClick={() => setMaand(new Date(maand.getFullYear(), maand.getMonth() + 1, 1))}
          aria-label="Volgende maand"
        >
          <IconChevronRechts />
        </button>
      </div>
      <div className="hs-minikalender__raster" role="grid" aria-label="Datum kiezen">
        <span className="hs-minikalender__wk">wk</span>
        {['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'].map((d) => (
          <span key={d} className="hs-minikalender__wd">
            {d}
          </span>
        ))}
        {dagen.map((dag, i) => (
          <MinikalenderDag
            key={i}
            dag={dag}
            week={i % 7 === 0 ? weeknummer(dag) : null}
            buiten={dag.getMonth() !== maand.getMonth()}
            vandaag={zelfdeDag(dag, vandaag)}
            inBeeld={inBeeld(dag)}
            stip={metAfspraak.has(dag.toDateString())}
            onKies={onKies}
          />
        ))}
      </div>
    </section>
  );
}

function MinikalenderDag({
  dag,
  week,
  buiten,
  vandaag,
  inBeeld,
  stip,
  onKies,
}: {
  dag: Date;
  week: number | null;
  buiten: boolean;
  vandaag: boolean;
  inBeeld: boolean;
  stip: boolean;
  onKies: (dag: Date) => void;
}) {
  return (
    <>
      {week != null ? <span className="hs-minikalender__wk">{week}</span> : null}
      <button
        type="button"
        className={['hs-minikalender__dag', buiten ? 'buiten' : '', vandaag ? 'vandaag' : '', inBeeld ? 'inbeeld' : '']
          .filter(Boolean)
          .join(' ')}
        onClick={() => onKies(dag)}
        aria-label={weekdagLang.format(dag)}
        aria-current={vandaag ? 'date' : undefined}
      >
        {dag.getDate()}
        {stip ? <span className="hs-minikalender__stip" /> : null}
      </button>
    </>
  );
}

/**
 * Wat nog in te plannen is, gegroepeerd zoals de houtstaat: per bouwnummer en sectie.
 *
 * Elke groep zegt hoeveel karren en uren erin zitten, zodat de werkvoorbereider ziet
 * waar het werk ligt voordat hij een groep openklapt. Een kar is één regel: het nummer
 * binnen de sectie, de naam, en rechts de uren die hij in de agenda krijgt. Een volle
 * streep in de bootkleur is vrijgegeven, een gestreepte is voorlopig (nog op concept,
 * alleen begroot), net als in de agenda. Groepen met een vrijgegeven kar staan open.
 */
function Wachtrij({
  karren,
  eenBoot,
  zoek,
  onZoek,
  soort,
  onSoort,
  kleurVan,
  magWijzigen,
  actief,
  onSleep,
}: {
  karren: AgendaKar[];
  /** Er is op één bouwnummer gefilterd: dan hoeft het niet in elke groepskop. */
  eenBoot: boolean;
  zoek: string;
  onZoek: (zoek: string) => void;
  soort: Soort;
  onSoort: (soort: Soort) => void;
  kleurVan: Map<string, string>;
  magWijzigen: boolean;
  actief: boolean;
  onSleep: (e: React.PointerEvent, kar: AgendaKar) => void;
}) {
  const [geopend, setGeopend] = useState<Record<string, boolean>>({});
  const term = zoek.trim().toLowerCase();

  const telling: Record<Exclude<Soort, 'alle'>, number> = {
    vrijgegeven: karren.filter((k) => !isVoorlopig(k)).length,
    voorlopig: karren.filter(isVoorlopig).length,
  };
  const rang = (k: AgendaKar) => (k.status === 'produceren' ? 0 : k.status === 'on-hold' ? 1 : 2);
  const passend = karren
    .filter((k) => soort === 'alle' || (soort === 'voorlopig') === isVoorlopig(k))
    .filter((k) => !term || `${k.id} ${k.karNaam} ${k.ruimte} ${k.mspCode ?? ''}`.toLowerCase().includes(term));

  const perGroep = new Map<string, AgendaKar[]>();
  for (const k of passend) {
    const sleutel = `${k.bouwnummer}-${k.sectie}`;
    perGroep.set(sleutel, [...(perGroep.get(sleutel) ?? []), k]);
  }
  const groepen = [...perGroep]
    .map(([sleutel, lijst]) => ({
      sleutel,
      bouwnummer: lijst[0]!.bouwnummer,
      sectie: lijst[0]!.sectie,
      karren: lijst.sort((a, b) => rang(a) - rang(b) || a.id.localeCompare(b.id)),
      uren: lijst.reduce((n, k) => n + k.urenAfspraak, 0),
      vrijgegeven: lijst.some((k) => k.status === 'produceren'),
    }))
    .sort((a, b) => a.sleutel.localeCompare(b.sleutel));

  // Open: wat je zelf opent; anders bij zoeken alles, en anders de groepen met werk
  // dat klaar is om te plannen. Is er maar één groep, dan staat die open.
  const isOpen = (g: (typeof groepen)[number]) =>
    geopend[g.sleutel] ?? (Boolean(term) || g.vrijgegeven || groepen.length === 1);

  const SOORTEN: Array<{ waarde: Soort; label: string }> = [
    { waarde: 'alle', label: 'Alle' },
    { waarde: 'vrijgegeven', label: 'Vrijgegeven' },
    { waarde: 'voorlopig', label: 'Voorlopig' },
  ];

  return (
    <section className={`hs-agenda__blok hs-wachtrij${actief ? ' actief' : ''}`} data-wachtrij="">
      <div className="hs-wachtrij__kop">
        <p className="hs-annotatie hs-agenda__blokkop">Nog in te plannen</p>
        <span className="hs-wachtrij__totaal">{passend.length}</span>
      </div>
      {actief ? (
        <p className="hs-wachtrij__uitleg hs-wachtrij__uitleg--terug">Loslaten haalt de kar uit de planning.</p>
      ) : magWijzigen ? (
        <p className="hs-wachtrij__uitleg">Sleep een kar naar de agenda. Hij krijgt de lengte van zijn uren, in werkuren.</p>
      ) : null}

      <label className="hs-zoekveld hs-zoekveld--klein">
        <IconZoek maat={14} />
        <input
          type="search"
          value={zoek}
          onChange={(e) => onZoek(e.target.value)}
          placeholder="Kar, ruimte of MSP-code"
          aria-label="Zoeken in wat nog in te plannen is"
        />
      </label>
      <div className="hs-wachtrij__soorten" role="radiogroup" aria-label="Welke karren">
        {SOORTEN.map(({ waarde, label }) => (
          <button
            key={waarde}
            type="button"
            role="radio"
            aria-checked={soort === waarde}
            className={`hs-wachtrij__soort hs-wachtrij__soort--${waarde}${soort === waarde ? ' actief' : ''}`}
            onClick={() => onSoort(waarde)}
          >
            {label}
            {/* Het totaal staat al in de kop; bij Alle zou het dubbel staan. */}
            {waarde === 'alle' ? null : <span className="hs-wachtrij__aantal">{telling[waarde]}</span>}
          </button>
        ))}
      </div>

      <ul className="hs-wachtrij__lijst">
        {groepen.map((g) => {
          const open = isOpen(g);
          return (
            <li key={g.sleutel} className="hs-wachtrij__groep">
              <button
                type="button"
                className="hs-wachtrij__groepkop"
                aria-expanded={open}
                onClick={() => setGeopend((o) => ({ ...o, [g.sleutel]: !open }))}
              >
                <IconChevronRechts maat={14} className={`hs-wachtrij__pijl${open ? ' open' : ''}`} />
                {eenBoot ? null : (
                  <span className="hs-wachtrij__stip" style={{ '--boot': kleurVan.get(g.bouwnummer) } as CSSProperties} />
                )}
                <span className="hs-wachtrij__groepnaam">
                  {eenBoot ? '' : `${bouwnummerKort(g.bouwnummer)} · `}sectie {g.sectie}
                </span>
                <span className="hs-wachtrij__som">
                  {g.karren.length} · {uren(g.uren)}
                </span>
              </button>
              {open ? (
                <ul className="hs-wachtrij__karren">
                  {g.karren.slice(0, MAX_PER_GROEP).map((k) => (
                    <li key={k.id}>
                      <div
                        className={`hs-wachtrij__kar${k.inplanbaar ? '' : ' geblokkeerd'}${isVoorlopig(k) ? ' voorlopig' : ''}`}
                        style={{ '--boot': kleurVan.get(k.bouwnummer) } as CSSProperties}
                        onPointerDown={magWijzigen && k.inplanbaar ? (e) => onSleep(e, k) : undefined}
                        title={wachtTitel(k, magWijzigen)}
                      >
                        {magWijzigen && k.inplanbaar ? <IconGreep className="hs-wachtrij__greep" /> : null}
                        <span className="hs-mono hs-wachtrij__code">{k.id.split('-').slice(2).join('-')}</span>
                        <span className="hs-wachtrij__naam">{karTitel(k)}</span>
                        {k.status === 'on-hold' ? <span className="hs-chip hs-chip--hold">on-hold</span> : null}
                        <span className="hs-wachtrij__uren">{uren(k.urenAfspraak)}</span>
                      </div>
                    </li>
                  ))}
                  {g.karren.length > MAX_PER_GROEP ? (
                    <li className="hs-wachtrij__meer">
                      Nog {g.karren.length - MAX_PER_GROEP} karren; zoek op nummer of ruimte.
                    </li>
                  ) : null}
                </ul>
              ) : null}
            </li>
          );
        })}
        {groepen.length === 0 ? (
          <li className="hs-wachtrij__meer">
            {term ? 'Geen kar gevonden.' : soort === 'vrijgegeven' ? 'Nog geen vrijgegeven karren.' : 'Alles is ingepland.'}
          </li>
        ) : null}
      </ul>
    </section>
  );
}

/** Wat de tooltip van een kar in de wachtrij vertelt: waarom hij (niet) kan, en waar de uren vandaan komen. */
function wachtTitel(k: AgendaKar, magWijzigen: boolean): string {
  if (!k.inplanbaar) return k.onHold ? `On-hold: ${k.onHold.reden}` : 'Niet in te plannen';
  const bron =
    k.urenHoutstaat != null
      ? `${uren(k.urenHoutstaat)} uit de houtstaat`
      : k.begroot.Machinale
        ? `${uren(k.begroot.Machinale)} machinale, begroot in MS Project`
        : `${uren(k.urenAfspraak)} begroot`;
  const status = isVoorlopig(k) ? 'Voorlopig: nog niet vrijgegeven' : 'Vrijgegeven voor productie';
  return `${k.id} · ${status} · ${bron}${magWijzigen ? ' · sleep naar de agenda' : ''}`;
}

// ── Weergave-instellingen ───────────────────────────────────────────────────

function Weergaveopties({
  voorkeur,
  onWijzig,
}: {
  voorkeur: Voorkeur;
  onWijzig: (wijziging: Partial<Voorkeur>) => void;
}) {
  const [open, setOpen] = useState(false);
  const houder = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const klik = (e: MouseEvent) => {
      if (!houder.current?.contains(e.target as Node)) setOpen(false);
    };
    const toets = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', klik);
    window.addEventListener('keydown', toets);
    return () => {
      window.removeEventListener('mousedown', klik);
      window.removeEventListener('keydown', toets);
    };
  }, [open]);

  return (
    <div className="hs-weergaveopties" ref={houder}>
      <button type="button" className="hs-knop" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        Weergave <IconChevronOnder />
      </button>
      {open ? (
        <div className="hs-weergaveopties__paneel" role="dialog" aria-label="Weergave-instellingen">
          <p className="hs-annotatie hs-muted">Tijdschaal</p>
          <div className="hs-segment" role="radiogroup" aria-label="Tijdschaal">
            {([60, 30, 15] as const).map((t) => (
              <button
                key={t}
                type="button"
                role="radio"
                aria-checked={voorkeur.tijdschaal === t}
                className={`hs-segment__optie${voorkeur.tijdschaal === t ? ' actief' : ''}`}
                onClick={() => onWijzig({ tijdschaal: t })}
              >
                {t} min
              </button>
            ))}
          </div>
          <Schakelaar
            label="Alleen werkuren"
            uitleg={`${klok(WERKUREN.begin)} tot ${klok(WERKUREN.eind)}, maandag tot en met vrijdag`}
            aan={voorkeur.alleenWerkuren}
            onWissel={(aan) => onWijzig({ alleenWerkuren: aan })}
          />
          <Schakelaar
            label="Bouwnummers naast elkaar"
            uitleg="Elk bouwnummer een eigen kolom binnen de dag"
            aan={voorkeur.naastElkaar}
            onWissel={(aan) => onWijzig({ naastElkaar: aan })}
          />
        </div>
      ) : null}
    </div>
  );
}

// ── Details van een afspraak ─────────────────────────────────────────────────

function Details({
  kar,
  rect,
  kleur,
  magWijzigen,
  onSluit,
  onUitPlanning,
  onHoutstaat,
}: {
  kar: AgendaKar;
  rect: DOMRect;
  kleur: string;
  magWijzigen: boolean;
  onSluit: () => void;
  onUitPlanning: () => void;
  onHoutstaat: () => void;
}) {
  const paneel = useRef<HTMLDivElement>(null);
  const [plek, setPlek] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const breed = 360;
    const hoog = paneel.current?.offsetHeight ?? 320;
    const left = rect.right + 12 + breed < window.innerWidth ? rect.right + 12 : Math.max(12, rect.left - breed - 12);
    const top = Math.min(Math.max(12, rect.top), window.innerHeight - hoog - 12);
    setPlek({ left, top });
  }, [rect]);

  useEffect(() => {
    paneel.current?.focus();
    const klik = (e: MouseEvent) => {
      if (!paneel.current?.contains(e.target as Node)) onSluit();
    };
    const toets = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onSluit();
    };
    window.addEventListener('mousedown', klik);
    window.addEventListener('keydown', toets);
    return () => {
      window.removeEventListener('mousedown', klik);
      window.removeEventListener('keydown', toets);
    };
  }, [onSluit]);

  const start = kar.start ? leesTijdstip(kar.start) : null;
  const eind = kar.eind ? leesTijdstip(kar.eind) : null;
  const begroot = Object.entries(kar.begroot).filter(([, u]) => u);

  return (
    <div
      ref={paneel}
      className="hs-details"
      role="dialog"
      aria-label={`Kar ${kar.id}`}
      tabIndex={-1}
      style={{ left: plek?.left ?? -9999, top: plek?.top ?? 0, '--boot': kleur } as CSSProperties}
    >
      <div className="hs-details__kop">
        <span className="hs-details__balk" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <p className="hs-annotatie hs-muted">Bouwnummer {kar.bouwnummer}</p>
          <p className="hs-details__titel hs-mono">{kar.id}</p>
          {karTitel(kar) ? <p className="hs-muted">{karTitel(kar)}</p> : null}
        </div>
        <button type="button" className="hs-icoonknop" onClick={onSluit} aria-label="Sluiten">
          <IconKruis maat={18} />
        </button>
      </div>

      <dl className="hs-details__lijst">
        <dt>Status</dt>
        <dd>
          {kar.status === 'produceren'
            ? 'Vrijgegeven voor productie'
            : kar.status === 'on-hold'
              ? `On-hold${kar.onHold ? `: ${kar.onHold.reden}` : ''}`
              : 'Voorlopig: de houtstaat is nog niet vrijgegeven'}
        </dd>
        {start && eind ? (
          <>
            <dt>Begint</dt>
            <dd>{weekdagLang.format(start)} {klokVan(start)}</dd>
            <dt>Eindigt</dt>
            <dd>{weekdagLang.format(eind)} {klokVan(eind)}</dd>
            <dt>Duur</dt>
            <dd>{uren(werkminutenTussen(start, eind, WERKUREN) / 60)} in werkuren</dd>
          </>
        ) : null}
        <dt>Houtstaat</dt>
        <dd>
          {kar.onderdelen > 0
            ? `${kar.onderdelen} onderdelen · ${kar.stuks} stuks · ${uren(kar.urenHoutstaat)} resterend`
            : 'nog geen onderdelen geïmporteerd'}
        </dd>
        {kar.mspCode ? (
          <>
            <dt>MS Project</dt>
            <dd className="hs-mono">{kar.mspCode}</dd>
          </>
        ) : null}
      </dl>

      {kar.bezetting.length > 0 ? (
        <div className="hs-strip hs-strip--hoog" title="Resterend werk per afdeling volgens de houtstaat">
          {kar.bezetting.map((b) => (
            <span key={b.afdeling} className="hs-strip__deel" style={{ flex: b.deel, background: b.kleur }} title={`${b.afdeling}: ${uren(b.uren)}`} />
          ))}
        </div>
      ) : null}

      {begroot.length > 0 ? (
        <div>
          <p className="hs-annotatie hs-muted">Begroot in MS Project</p>
          <ul className="hs-details__begroot">
            {begroot.map(([afdeling, u]) => (
              <li key={afdeling}>
                <span>{afdeling}</span>
                <span className="hs-mono">{uren(u)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="hs-knoprij">
        <button type="button" className="hs-knop" onClick={onHoutstaat}>
          Houtstaat openen
        </button>
        {magWijzigen && kar.start ? (
          <button type="button" className="hs-knop" onClick={onUitPlanning}>
            Uit de planning halen
          </button>
        ) : null}
      </div>
    </div>
  );
}
